import type { IContinueWatchingCardEpisode } from '@setplex/tria-api'
import type { MouseEvent } from 'react'
import type { IContinueWatchingCardFormat } from '~/entities/continue-watching'
import type { ViewRow } from '../../model/components/index.h'

import { CardCorners } from "/app/libraries/arcana/src/blocks/Cards/index.ts"
import { ContinueWatchCard } from "/app/libraries/arcana/src/blocks/Cards/ContinueWatchCard/index.ts"

import { MediaTypes } from '@setplex/tria-api'
import { useUnit } from 'effector-react'
import { useCallback, useMemo } from 'react'
import { isPaid } from '~/features/payment'
import * as pinLockFeature from '~/features/pin-lock'
import { addQuery } from '~/shared/routes'
import {
  VodCardHorizontalCoversMap,
  VodCardVerticalCoversMap,
} from '~/shared/ui/components/cardCovers'
import { getCardFonts, getCWCardPadding } from '../lib/style'
import { ContinueWatchingActions } from './ContinueWatchingActions'
import { isVisibleCwRowItem } from './cw-card-visibility'

const categoryId = 0

export const CWCard = ({
  row,
  item,
}: {
  row: ViewRow
  item: IContinueWatchingCardFormat
}) => {
  const {
    progress,
    contentName,
    landscapeImageUrl,
    portraitImageUrl,
    cardDescription,
    viewedTime,
    contentId,
    blockedByAcl,
    labelPrice,
    rented,
  } = item

  const {
    appearance: {
      style: { posterSize, posterOrientation },
      title,
    },
  } = row

  const episodeItem =
    item.contentType === MediaTypes.EPISODE
      ? (item as IContinueWatchingCardEpisode)
      : null
  const tvShowId = episodeItem?.tvShowId
  const seasonId = episodeItem?.seasonId
  const isWithSeason = episodeItem?.isWithSeason === true
  const { isLocked, intercept } = useUnit({
    isLocked: pinLockFeature.$isLockedFn,
    intercept: pinLockFeature.intercept,
  })

  const id = item.contentType === MediaTypes.EPISODE ? tvShowId : item.contentId

  const locked = isLocked({
    id,
    locked: item.locked,
  })

  const handleSelectContinueWatching = useCallback(
    (event: MouseEvent<Element>) => {
      intercept({
        id,
        locked,
        name: contentName,
        '<event>': event,
      })
    },
    [id, intercept, contentName, locked]
  )

  const href = useMemo(
    () =>
      item.contentType === 'VOD'
        ? addQuery(`/player/movie`, {
            categoryId,
            categoryName: title,
            vodId: item.contentId,
          })
        : addQuery(`/player/series`, {
            categoryId,
            categoryName: title,
            tvshowId: tvShowId,
            seasonId: isWithSeason ? seasonId : undefined,
            episodeId: contentId,
          }),
    [
      contentId,
      item.contentId,
      item.contentType,
      isWithSeason,
      seasonId,
      title,
      tvShowId,
    ]
  )

  const detailsHref = useMemo(
    () =>
      item.contentType === MediaTypes.VOD
        ? addQuery(`/content/movie/${item.contentId}`, {
            fromCwActionsDetails: true,
          })
        : addQuery(`/content/series/${tvShowId}`, {
            seasonId: seasonId,
            episodeId: item.contentId,
            fromCwActionsDetails: true,
          }),
    [item.contentId, item.contentType, seasonId, tvShowId]
  )

  const posterUrl =
    posterOrientation === 'HORIZONTAL' ? landscapeImageUrl : portraitImageUrl

  const { titleFont, labelFont } = getCardFonts(posterSize)
  const padding = getCWCardPadding(posterSize)
  const covers =
    posterOrientation === 'HORIZONTAL'
      ? VodCardHorizontalCoversMap
      : VodCardVerticalCoversMap

  if (!isVisibleCwRowItem(item)) return null

  return (
    <a href={href}>
      <ContinueWatchingActions
        item={item}
        locked={Boolean(locked)}
        rowTitle={title}
        detailsHref={detailsHref}
      >
        <ContinueWatchCard
          onClick={handleSelectContinueWatching}
          corners={CardCorners.CORNERS_25}
          titleFont={titleFont}
          progress={progress}
          posterUrl={posterUrl}
          title={contentName ?? ''}
          description={cardDescription}
          durationTitle={viewedTime}
          locked={Boolean(locked)}
          lockedTitle={contentName}
          padding={padding}
          labelFont={labelFont}
          covers={covers}
          ageRatingLabel=''
          aclBlockedBy={blockedByAcl}
          rented={rented}
          paid={isPaid(item)}
          purchaseTitle={labelPrice}
        />
      </ContinueWatchingActions>
    </a>
  )
}
